import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { RootState } from '@/store'
import { NextStepCard } from '@/modules/next-steps/types/nextStepCardTypes'
import axios from 'axios'
import { errorSummary, RemoteLog } from '@/modules/common/utils/loggingUtils'

export type State = {
	cards: NextStepCard[]
	loading: boolean
}

export const state = (): State => ({
	cards: [],
	loading: false,
})

const mutations: MutationTree<State> = {
	setCards(state: State, cards: NextStepCard[]) {
		state.cards = cards ?? []
	},
	setLoading(state: State, loading: boolean) {
		state.loading = loading
	},
	removeCard(state: State, key: string) {
		state.cards = state.cards.filter(card => card.key !== key)
	},
}

const actions: ActionTree<State, RootState> = {
	async getNextStepCards({ commit }): Promise<void> {
		try {
			commit('setLoading', true)

			// The response interceptor unwraps the HTTP body; this endpoint returns a
			// bare (already ordered/resolved) array of next step cards.
			const cards = await axios.get<NextStepCard[]>('/next-steps')

			commit('setCards', cards)
		} catch (err: any) {
			if (err.handled) {
				return
			}
			RemoteLog.error({ message: 'Next Steps failed to load', context: errorSummary(err) })
		} finally {
			commit('setLoading', false)
		}
	},
	async deferCard({ commit, dispatch }, key: string): Promise<void> {
		// Optimistically remove so the card animates "leaving"...
		commit('removeCard', key)

		try {
			await axios.post(`/next-steps/${key}/defer`)
		} catch (err: any) {
			if (err.handled) {
				return
			}
			RemoteLog.error({ message: 'Next Step card deferral failed', context: errorSummary(err) })
		} finally {
			// ...then reload so the backend re-orders (the deferred card slides back
			// in at the bottom); on failure this restores the true server state.
			await dispatch('getNextStepCards')
		}
	},
	async dismissCard({ commit, dispatch }, key: string): Promise<void> {
		// Optimistically remove so the card animates "leaving".
		commit('removeCard', key)

		try {
			await axios.post(`/next-steps/${key}/dismiss`)
		} catch (err: any) {
			if (err.handled) {
				return
			}
			RemoteLog.error({ message: 'Next Step card dismissal failed', context: errorSummary(err) })
		} finally {
			// Reload the list; on failure this restores the true server state.
			await dispatch('getNextStepCards')
		}
	},
}

const getters: GetterTree<State, RootState> = {
	cards: (state: State) => state.cards,
	loading: (state: State) => state.loading,
}

const module: Module<State, RootState> = {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
}

export default module
